import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Eye } from 'lucide-react';
import toast from 'react-hot-toast';

import { notificationsApi } from '../../../api/notifications.js';
import { ApiError } from '../../../api/client.js';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { DataTable } from '../../../components/DataTable.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { fmt } from '../../../lib/formatters.js';
import { t } from '../../../lib/i18n.js';
import { TemplateEditorModal } from './TemplateEditorModal.jsx';

export function TemplatesTab({ canManage }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['notifications', 'templates'], queryFn: () => notificationsApi.listTemplates({ limit: 100 }) });
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications', 'templates'] });
    queryClient.invalidateQueries({ queryKey: ['notifications', 'templates', 'all'] });
  };

  const deleteMutation = useMutation({
    mutationFn: (id) => notificationsApi.removeTemplate(id),
    onSuccess: () => { toast.success('Template eliminato.'); invalidate(); setDeleteTarget(null); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const columns = [
    { key: 'name', header: 'Template', render: (r) => (
      <div className="flex flex-col">
        <span className="font-medium text-ink">{r.name}</span>
        <code className="text-xs text-ink-faint">{r.key}</code>
      </div>
    ) },
    { key: 'subject', header: 'Oggetto', render: (r) => <span className="text-xs text-ink-muted">{r.subject}</span> },
    { key: 'flags', header: 'Stato', render: (r) => (
      <span className="flex gap-1">
        <Badge tone={r.enabled ? 'success' : 'neutral'}>{r.enabled ? 'attivo' : 'disattivo'}</Badge>
        {r.isSystem && <Badge tone="info">sistema</Badge>}
      </span>
    ) },
    { key: 'updatedAt', header: 'Aggiornato', render: (r) => <span className="text-xs text-ink-muted">{fmt.dateTime(r.updatedAt)}</span> },
    { key: 'actions', header: '', align: 'right', render: (r) => (
      <div className="flex justify-end gap-0.5">
        {canManage && <button type="button" aria-label="Modifica" className="rounded-md p-1.5 text-ink-muted hover:bg-surface-subtle hover:text-ink" onClick={() => setEditing(r)}><Pencil className="h-4 w-4" /></button>}
        {canManage && r.key !== 'default' && <button type="button" aria-label="Elimina" className="rounded-md p-1.5 text-ink-muted hover:bg-danger/10 hover:text-danger" onClick={() => setDeleteTarget(r)}><Trash2 className="h-4 w-4" /></button>}
        {!canManage && <span className="inline-flex p-1.5 text-ink-faint"><Eye className="h-4 w-4" /></span>}
      </div>
    ) }
  ];

  return (
    <div className="flex flex-col gap-4">
      {canManage && (
        <div className="flex justify-end">
          <Button onClick={() => setCreating(true)}>Nuovo template</Button>
        </div>
      )}

      <DataTable columns={columns} rows={query.data?.items || []} loading={query.isLoading} empty={{ title: 'Nessun template' }} />

      <TemplateEditorModal open={creating} onClose={() => setCreating(false)} template={null} onSaved={invalidate} />
      <TemplateEditorModal open={Boolean(editing)} onClose={() => setEditing(null)} template={editing} onSaved={invalidate} />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Eliminare il template?"
        description={deleteTarget ? `"${deleteTarget.name}" sarà eliminato. Le regole che lo usano torneranno al template di default.` : ''}
        destructive
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget._id)}
      />
    </div>
  );
}
